/**
 * ProfileSettingsBackend - Unified storage for user profile settings
 * 
 * Handles reading/writing to profiles.settings in Supabase (cloud)
 * or localStorage (fallback when logged out).
 * 
 * Used by: FavoritesService, RecentPatchesService
 */

/**
 * Create a profile settings backend
 * @param {Object} authService - Auth service for cloud access
 * @returns {Object} Backend interface
 */
export function createProfileSettingsBackend(authService) {
    
    // ============= HELPERS =============
    
    function getClient() {
        return authService?.getSupabaseClient();
    }
    
    function getUserId() {
        return authService?.getCurrentUser()?.id;
    }
    
    function isCloudAvailable() {
        return !!(authService?.isLoggedIn() && getClient() && getUserId());
    }
    
    // ============= CLOUD OPERATIONS =============
    
    /**
     * Get a specific key from profiles.settings
     * @param {string} key - Settings key (e.g., 'favorites', 'recents')
     * @returns {Promise<*>} Value or null
     */
    async function get(key) {
        if (!isCloudAvailable()) {
            console.log(`[ProfileSettings] get(${key}) - not logged in`);
            return null;
        }
        
        try {
            const { data, error } = await getClient()
                .from('profiles')
                .select('settings')
                .eq('id', getUserId())
                .maybeSingle();
            
            if (error) {
                console.warn(`[ProfileSettings] get(${key}) error:`, error);
                return null;
            }
            
            return data?.settings?.[key] ?? null;
        } catch (e) {
            console.warn(`[ProfileSettings] get(${key}) exception:`, e);
            return null;
        }
    }
    
    /**
     * Set a specific key in profiles.settings (preserves other keys)
     * @param {string} key - Settings key
     * @param {*} value - Value to store
     * @returns {Promise<boolean>} Success
     */
    async function set(key, value) {
        if (!isCloudAvailable()) {
            console.log(`[ProfileSettings] set(${key}) - not logged in`);
            return false;
        }
        
        const client = getClient();
        const userId = getUserId();
        
        try {
            // Get current settings to preserve other keys
            const { data: profile } = await client
                .from('profiles')
                .select('settings')
                .eq('id', userId)
                .maybeSingle();
            
            const settings = profile?.settings || {};
            settings[key] = value;
            
            const { error } = await client
                .from('profiles')
                .update({ settings })
                .eq('id', userId);
            
            if (error) {
                console.warn(`[ProfileSettings] set(${key}) error:`, error);
                return false;
            }
            
            return true;
        } catch (e) {
            console.warn(`[ProfileSettings] set(${key}) exception:`, e);
            return false;
        }
    }
    
    /**
     * Get all settings
     * @returns {Promise<Object>} All settings or empty object
     */
    async function getAll() {
        if (!isCloudAvailable()) {
            return {};
        }
        
        try {
            const { data, error } = await getClient()
                .from('profiles')
                .select('settings')
                .eq('id', getUserId())
                .maybeSingle();
            
            if (error) {
                console.warn('[ProfileSettings] getAll error:', error);
                return {};
            }
            
            return data?.settings || {};
        } catch (e) {
            console.warn('[ProfileSettings] getAll exception:', e);
            return {};
        }
    }
    
    // ============= LOCAL STORAGE FALLBACK =============
    
    /**
     * Get from localStorage
     * @param {string} key - localStorage key
     * @returns {*} Parsed value or null
     */
    function getLocal(key) {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (e) {
            console.warn(`[ProfileSettings] getLocal(${key}) failed:`, e);
            return null;
        }
    }
    
    /**
     * Set to localStorage
     * @param {string} key - localStorage key
     * @param {*} value - Value to store (will be JSON stringified)
     * @returns {boolean} Success
     */
    function setLocal(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (e) {
            console.warn(`[ProfileSettings] setLocal(${key}) failed:`, e);
            return false;
        }
    }
    
    /**
     * Remove from localStorage
     * @param {string} key - localStorage key
     */
    function removeLocal(key) {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.warn(`[ProfileSettings] removeLocal(${key}) failed:`, e);
        }
    }
    
    /**
     * Check if user is logged in (for callers to decide which method to use)
     * @returns {boolean}
     */
    function isLoggedIn() {
        return isCloudAvailable();
    }
    
    return {
        // Cloud operations
        get,
        set,
        getAll,
        // Local fallback
        getLocal,
        setLocal,
        removeLocal,
        // Utility
        isLoggedIn
    };
}
